// src/app/core/iam/iam.api.ts
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  IamAPIOrganization,
  IamAttributes,
  IamCreateUser,
  ManagerInviteResponse,
  ManagersResponse,
  ResetPasswordRequest,
  UserAttributes,
} from '@core/iam/iam.models';
import { CRAPIError } from '@shared/models/error.models';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
// import models from shared/models if you have them

@Injectable({ providedIn: 'root' })
export class IamApiService {
  private readonly http = inject(HttpClient);

  // PC-IAM Endpoints
  private readonly baseIAMURI = environment.pcIAMBaseURL;
  private readonly baseCRURI = environment.chargeRepayBaseURL;
  private readonly crAttributesEndpoint: string = `${this.baseCRURI}/v2/organizations/active`;

  private readonly iamUserAttributesEndpoint: string = `${this.baseIAMURI}/user/attributes`;
  private readonly iamUserEndpoint: string = `${this.baseIAMURI}/user`;
  /*
  private readonly forgotPasswordEndpoint: string = `${this.baseIAMURI}/user/credentials/reset-request`;
  */
  private readonly resetPasswordEndpoint: string = `${this.baseIAMURI}/user/credentials`;
  private readonly organizationEndpoint: string = `${this.baseCRURI}/v2/organizations`;
  private readonly managersEndpoint: string = `${this.baseCRURI}/v2/organization/managers`;

  public getIamOrganisations() {
    return this.http
      .get<IamAPIOrganization[]>(this.organizationEndpoint, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        catchError((error) => {
          return this.handleError(error);
        }),
      );
  }

  public getManagers() {
    return this.http
      .get<ManagersResponse>(this.managersEndpoint, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  public deleteManager(userId: string, isInvitationId: boolean = false) {
    const url = `${this.managersEndpoint}/${userId}`;
    return this.http
      .delete<void>(url, {
        headers: { 'Content-Type': 'application/json' },
        params: { is_invitation_id: isInvitationId },
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  public inviteManager(userEmail: string, locale: string) {
    const body = [
      {
        locale,
        user_email: userEmail,
      },
    ];
    return this.http
      .post<ManagerInviteResponse[]>(this.managersEndpoint, body, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  public updateActiveOrganization(orgId: string) {
    return this.http
      .put(this.crAttributesEndpoint, { active_organization: orgId })
      .pipe(catchError((error) => this.handleError(error)));
  }

  public updateIamAttributes(attributes: IamAttributes) {
    return this.http
      .put(this.iamUserEndpoint, attributes)
      .pipe(catchError((error) => this.handleError(error)));
  }

  public updateIamLanguage(attributes: UserAttributes) {
    return this.http
      .put(this.iamUserAttributesEndpoint, { attributes: attributes })
      .pipe(catchError((error) => this.handleError(error)));
  }

  public createUser(payload: IamCreateUser) {
    return this.http
      .post<void>(this.iamUserEndpoint, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  public requestPasswordReset(email: string) {
    const url = this.baseIAMURI + `/user/credentials/reset-request`;
    return this.http
      .post<void>(url, { email })
      .pipe(catchError((error) => this.handleError(error)));
  }

  public resetPassword(request: ResetPasswordRequest) {
    return this.http
      .put<void>(this.resetPasswordEndpoint, request)
      .pipe(catchError((error) => this.handleError(error)));
  }

  public acceptInvitation(organizationId: string, invitationId: string) {
    const url = `${this.baseIAMURI}/organizations/${organizationId}/invitations/${invitationId}`;
    return this.http
      .put<void>(url, { action: 'accept' }, { headers: { 'Content-Type': 'application/json' } })
      .pipe(catchError((error) => this.handleError(error)));
  }

  private handleError(error: HttpErrorResponse) {
    console.error(error?.error?.error?.id, error?.status);

    const errorMessage: CRAPIError = {
      statusCode: error?.status ?? 0,
      error_code: error?.error?.error?.id ? error?.error?.error?.id : 'UNKNOWN_ERROR',
    };

    return throwError(() => errorMessage);
  }
}
